import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { blogPosts as legacyBlogPosts } from "./blogPosts";

const EXPORT_URL = (process.env.REACT_APP_FTOJ_PUBLISHED_BLOG_URL || "").trim();

const BlogCatalogContext = createContext(null);

function formatExportDate(value) {
  if (!value) return "";
  const parsed = Date.parse(value);
  if (Number.isNaN(parsed)) return String(value);
  return new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZone: "America/New_York",
  }).format(new Date(parsed));
}

function firstParagraph(body) {
  const paragraph = String(body || "")
    .split("\n\n")
    .map((block) => block.trim())
    .find((block) => block && !block.startsWith("#"));
  if (!paragraph) return "";
  return paragraph.replace(/\n/g, " ").slice(0, 200);
}

function mapFtojPost(raw) {
  const content = String(raw.content || "").trim();
  return {
    slug: String(raw.slug || "").trim(),
    title: String(raw.title || raw.slug || "Untitled").trim(),
    date:
      formatExportDate(raw.date) ||
      formatExportDate(raw.publishedAt) ||
      "",
    author: String(raw.author || "Jones & Booth Law").trim(),
    category: String(raw.category || "General").trim(),
    excerpt: String(raw.excerpt || firstParagraph(content)).trim(),
    content,
    tags: Array.isArray(raw.tags) ? raw.tags.map(String) : [],
  };
}

function sortByDateDesc(posts) {
  return [...posts].sort((a, b) => {
    const dateA = Date.parse(a.date) || 0;
    const dateB = Date.parse(b.date) || 0;
    return dateB - dateA;
  });
}

export function mergeBlogCatalog(legacyPosts, ftojExport) {
  const bySlug = new Map();
  for (const post of legacyPosts) {
    bySlug.set(post.slug, post);
  }

  let nextId = Math.max(0, ...legacyPosts.map((p) => Number(p.id) || 0)) + 1;
  const ftojPosts = Array.isArray(ftojExport?.posts) ? ftojExport.posts : [];

  for (const raw of ftojPosts) {
    const mapped = mapFtojPost(raw);
    if (!mapped.slug) continue;
    const existing = bySlug.get(mapped.slug);
    if (existing) {
      bySlug.set(mapped.slug, { ...mapped, id: existing.id });
    } else {
      bySlug.set(mapped.slug, { ...mapped, id: nextId });
      nextId += 1;
    }
  }

  return sortByDateDesc([...bySlug.values()]);
}

export function getBlogPostBySlugFrom(posts, slug) {
  return posts.find((post) => post.slug === slug);
}

export function getRelatedPostsFrom(posts, currentPostSlug, limit = 2) {
  const current = getBlogPostBySlugFrom(posts, currentPostSlug);
  if (!current) {
    return posts.filter((post) => post.slug !== currentPostSlug).slice(0, limit);
  }
  const sameCategory = posts.filter(
    (post) =>
      post.slug !== currentPostSlug && post.category === current.category
  );
  const pool =
    sameCategory.length >= limit
      ? sameCategory
      : posts.filter((post) => post.slug !== currentPostSlug);
  return pool.slice(0, limit);
}

async function fetchFtojExport(url) {
  if (!url) return null;
  const res = await fetch(url, { cache: "no-cache" });
  if (!res.ok) {
    throw new Error(`FTOJ blog export HTTP ${res.status}`);
  }
  return res.json();
}

export function BlogCatalogProvider({ children }) {
  const [posts, setPosts] = useState(legacyBlogPosts);
  const [loading, setLoading] = useState(Boolean(EXPORT_URL));
  const [error, setError] = useState("");
  const [exportedAt, setExportedAt] = useState(null);

  useEffect(() => {
    if (!EXPORT_URL) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        const data = await fetchFtojExport(EXPORT_URL);
        if (cancelled) return;
        setPosts(mergeBlogCatalog(legacyBlogPosts, data));
        setExportedAt(data?.exportedAt || null);
        setError("");
      } catch (err) {
        if (cancelled) return;
        console.warn("FTOJ blog export unavailable; using prod_blog only", err);
        setPosts(legacyBlogPosts);
        setError(err.message || "export unavailable");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const categories = useMemo(
    () => [...new Set(posts.map((post) => post.category))].sort(),
    [posts]
  );

  const value = useMemo(
    () => ({
      posts,
      categories,
      loading,
      error,
      exportedAt,
      exportUrl: EXPORT_URL || null,
      legacyOnly: !EXPORT_URL,
    }),
    [posts, categories, loading, error, exportedAt]
  );

  return (
    <BlogCatalogContext.Provider value={value}>
      {children}
    </BlogCatalogContext.Provider>
  );
}

export function useBlogCatalog() {
  const ctx = useContext(BlogCatalogContext);
  if (!ctx) {
    throw new Error("useBlogCatalog must be used within BlogCatalogProvider");
  }
  return ctx;
}

export { legacyBlogPosts };
